/**
 * NotificationPanel - Slide-out Notification Center
 * Premium notification UI with grouping, actions, and preferences
 */

import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Bell,
  BellOff,
  Settings,
  Check,
  CheckCheck,
  Trash2,
  AlertCircle,
  Info,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Plane,
  Calendar,
  Users,
  DollarSign,
  ChevronRight,
  Inbox,
  Volume2,
  VolumeX,
} from 'lucide-react';
import type {
  Notification,
  NotificationType,
  NotificationPriority,
  NotificationGroup,
} from './types';
import { useNotificationStore, selectUnreadCount } from './notificationStore';

interface NotificationPanelProps {
  userId: string;
  onNotificationClick?: (notification: Notification) => void;
  className?: string;
}

const typeIcons: Record<NotificationType, React.ElementType> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
  queue_item: Inbox,
  schedule_change: Calendar,
  ticket_deadline: Clock,
  price_alert: DollarSign,
  customer_match: Users,
  system: Settings,
};

const typeColors: Record<NotificationType, { bg: string; text: string; border: string }> = {
  info: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  success: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  warning: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  error: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  queue_item: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  schedule_change: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
  ticket_deadline: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  price_alert: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  customer_match: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
  system: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
};

const priorityColors: Record<NotificationPriority, string> = {
  low: 'bg-gray-400',
  normal: 'bg-blue-400',
  high: 'bg-amber-400',
  urgent: 'bg-red-500',
};

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  userId,
  onNotificationClick,
  className = '',
}) => {
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [showSettings, setShowSettings] = useState(false);

  const {
    notifications,
    isPanelOpen,
    error,
    closePanel,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAll,
    soundEnabled,
    setSoundEnabled,
    soundVolume,
    setSoundVolume,
    requestBrowserPermission,
    browserPermission,
  } = useNotificationStore();

  const unreadCount = useNotificationStore(selectUnreadCount);

  useEffect(() => {
    if (!isPanelOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePanel();
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [closePanel, isPanelOpen]);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications(userId);
  }, [userId, fetchNotifications]);

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    if (filter === 'unread') {
      return notifications.filter((n) => !n.read);
    }
    return notifications;
  }, [notifications, filter]);

  // Group notifications by date
  const groupedNotifications = useMemo(() => {
    const groups: NotificationGroup[] = [];
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    filteredNotifications.forEach((notification) => {
      const notifDate = new Date(notification.createdAt).toDateString();
      let groupLabel: string;

      if (notifDate === today) {
        groupLabel = 'Today';
      } else if (notifDate === yesterday) {
        groupLabel = 'Yesterday';
      } else {
        groupLabel = new Date(notification.createdAt).toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
        });
      }

      const existingGroup = groups.find((g) => g.date === groupLabel);
      if (existingGroup) {
        existingGroup.notifications.push(notification);
      } else {
        groups.push({ date: groupLabel, notifications: [notification] });
      }
    });

    return groups;
  }, [filteredNotifications]);

  // Format relative time
  const formatRelativeTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    onNotificationClick?.(notification);
  };

  return (
    <AnimatePresence>
      {isPanelOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 z-40"
            aria-hidden="true"
            onClick={(event) => {
              if (event.target === event.currentTarget) closePanel();
            }}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            role="dialog"
            aria-modal="true"
            aria-label="Notifications"
            className={`fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col ${className}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Bell className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Notifications</h2>
                  <p className="text-sm text-gray-500">
                    {unreadCount} unread
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  type="button"
                  aria-label="Notification settings"
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Settings className="w-5 h-5" />
                </button>
                <button
                  onClick={closePanel}
                  type="button"
                  aria-label="Close notifications"
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {error && (
              <div role="alert" className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Settings Panel */}
            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-b border-gray-100 overflow-hidden"
                >
                  <div className="p-4 space-y-4">
                    {/* Browser Notifications */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Bell className="w-5 h-5 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            Browser Notifications
                          </div>
                          <div className="text-xs text-gray-500">
                            {browserPermission === 'granted'
                              ? 'Enabled'
                              : browserPermission === 'denied'
                              ? 'Blocked in browser'
                              : 'Not enabled'}
                          </div>
                        </div>
                      </div>
                      {browserPermission !== 'granted' && browserPermission !== 'denied' && (
                        <button
                          onClick={requestBrowserPermission}
                          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          Enable
                        </button>
                      )}
                    </div>

                    {/* Sound */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {soundEnabled ? (
                          <Volume2 className="w-5 h-5 text-gray-400" />
                        ) : (
                          <VolumeX className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            Sound
                          </div>
                          <div className="text-xs text-gray-500">
                            {soundEnabled ? `${soundVolume}%` : 'Muted'}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setSoundEnabled(!soundEnabled)}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          soundEnabled ? 'bg-blue-500' : 'bg-gray-300'
                        }`}
                      >
                        <motion.div
                          animate={{ x: soundEnabled ? 24 : 2 }}
                          className="w-5 h-5 bg-white rounded-full shadow"
                        />
                      </button>
                    </div>

                    {/* Volume Slider */}
                    {soundEnabled && (
                      <div className="pl-8">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={soundVolume}
                          onChange={(e) => setSoundVolume(parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Filter & Actions */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    filter === 'all'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('unread')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    filter === 'unread'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Unread ({unreadCount})
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => markAllAsRead()}
                  type="button"
                  aria-label="Mark all notifications as read"
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-4 h-4" />
                </button>
                <button
                  onClick={() => clearAll()}
                  type="button"
                  aria-label="Clear all notifications"
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Clear all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto">
              {groupedNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <BellOff className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700">
                    {filter === 'unread' ? 'All caught up!' : 'No notifications'}
                  </h3>
                  <p className="text-gray-500 mt-1">
                    {filter === 'unread'
                      ? 'You have no unread notifications'
                      : 'Notifications will appear here'}
                  </p>
                </div>
              ) : (
                groupedNotifications.map((group) => (
                  <div key={group.date}>
                    <div className="sticky top-0 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {group.date}
                    </div>
                    {group.notifications.map((notification, index) => {
                      const Icon = typeIcons[notification.type] || Bell;
                      const colors = typeColors[notification.type] || typeColors.info;

                      return (
                        <motion.div
                          key={notification.id}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => handleNotificationClick(notification)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleNotificationClick(notification);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-label={`${notification.read ? 'Read' : 'Unread'} notification: ${notification.title}`}
                          className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                            !notification.read ? 'bg-blue-50/50' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Priority indicator */}
                            <div
                              className={`w-1.5 h-1.5 rounded-full mt-2 ${
                                priorityColors[notification.priority]
                              }`}
                            />

                            {/* Icon */}
                            <div
                              className={`p-2 rounded-lg ${colors.bg} flex-shrink-0`}
                            >
                              <Icon className={`w-4 h-4 ${colors.text}`} />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <h4
                                  className={`text-sm font-medium ${
                                    notification.read ? 'text-gray-700' : 'text-gray-900'
                                  }`}
                                >
                                  {notification.title}
                                </h4>
                                <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                                  {formatRelativeTime(notification.createdAt)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                                {notification.message}
                              </p>

                              {/* Actions */}
                              {notification.actions && notification.actions.length > 0 && (
                                <div className="flex items-center gap-2 mt-2">
                                  {notification.actions.map((action) => (
                                    <button
                                      key={action.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Handle action
                                      }}
                                      className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                                        action.primary
                                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                                          : action.destructive
                                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                      }`}
                                    >
                                      {action.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Dismiss */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissNotification(notification.id);
                              }}
                              type="button"
                              aria-label={`Dismiss notification: ${notification.title}`}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationPanel;
